import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Popover,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import TextField from 'components/text-field';
import { format } from 'date-fns';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import useAlert from 'utils/alert';
import { usePolicyInfoContext } from '../contexts/policyInfoContext';

import { createEndorsement } from '../graphql/mutations';
import useSearchEndorsement from '../services/useSearchEndorsement';
import { TextButton } from 'components/TextButton';

const useStyles = makeStyles((theme: any) => ({
  addButton: {
    textTransform: 'capitalize',
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.common.white,
    },
  },
  addButtonIcon: { marginRight: theme.spacing(1) },
  cardContainer: {
    height: 250,
    overflow: 'auto',
  },
  cardItemContainer: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  cardItemSelected: { backgroundColor: theme.palette.background.default },
  cardText: { color: theme.palette.primary.darker },
  cardButtonContainer: {
    padding: 0,
    borderTopWidth: 1,
    borderTopColor: theme.palette.input.border,
    borderTopStyle: 'solid',
  },
  cardButton: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    display: 'flex',
    justifyContent: 'space-between',
  },
  cardButtonText: { color: theme.palette.tab.offselected },
  icon: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    fontSize: '0.875rem',
    '&:hover': { color: theme.palette.common.black },
  },
}));

const EndorsementSearch = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { options, setLatestUpdate, selectedTab, endorsements, setEndorsements } =
		usePolicyInfoContext();
  const selectedPolicy: 'owners_policy_tab' | 'lenders_policy_tab' = selectedTab?.id!;
  const {
    endorsements: endorsementsOptions,
    handleOnChange,
    search,
  } = useSearchEndorsement(
    options.endorsements || [],
    selectedPolicy === 'lenders_policy_tab'
      ? endorsements.lendersEndorsements
      : endorsements.ownersEndorsements
  );
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const openAddButton = Boolean(anchorEl);
  const { id: orderId } = useParams<{ id: string }>();
  const showAlert = useAlert();

  const { mutate: creatEndorsementMutation, ...creatEndorsementMutationResponse } =
		useMutation(createEndorsement);

  const handleClickAddButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseAddButton = () => {
    setAnchorEl(null);
  };

  const handleAddendorsement = useCallback(
    ({ code }: { code: string }) => {
      const kind = selectedPolicy === 'owners_policy_tab' ? 'owners' : 'lenders';

      creatEndorsementMutation(
        {
          id: orderId,
          kind,
          code,
        },
        {
          onSuccess: (data) => {
            setEndorsements({
              lendersEndorsements:
								data.createOrderPolicyEndorsement.lenders_policy.endorsements ?? [],
              ownersEndorsements:
								data.createOrderPolicyEndorsement.owners_policy.endorsements ?? [],
            });
            creatEndorsementMutationResponse.reset();
            showAlert(t('policyInfoAndRates:endorsementAdded'), 'success');
            if (setLatestUpdate) {
              const now = format(new Date(), 'hh:mm');
              setLatestUpdate({ time: now, type: 'success', message: `updated at  ${now}` });
            }
          },
          onError: (error) => {
            if (error instanceof Error && error.message.includes('Existing')) {
              showAlert(t('policyInfoAndRates:endorsementExisting'), 'error');
            }
          },
        }
      );
    },
    [creatEndorsementMutation, orderId, selectedPolicy, creatEndorsementMutationResponse, setEndorsements, setLatestUpdate, showAlert, t]
  );

  return (
    <Grid item>
      <TextButton
        text={t('common:add')}
        icon="plus"
        textColor="#FB8C00"
        size="small"
        onClick={handleClickAddButton}
      />

      <Popover
        open={openAddButton}
        anchorEl={anchorEl}
        onClose={handleCloseAddButton}
        anchorOrigin={{
				  vertical: 'bottom',
				  horizontal: 'right',
        }}
        transformOrigin={{
				  vertical: 'top',
				  horizontal: 'right',
        }}
      >
        <Card>
          <CardContent style={{ paddingBottom: 0 }}>
            <TextField
              variant="standard"
              name="search-endorsement"
              value={search}
              onChange={handleOnChange}
              InputProps={{
							  endAdornment: <FontAwesomeIcon icon="search" size="sm" className={classes.icon} />,
              }}
            />
          </CardContent>

          <CardContent
            className={clsx({ [classes.cardContainer]: endorsementsOptions.length > 3 })}
          >
            {endorsementsOptions.length === 0 && (
            <Typography className={classes.cardText}>
              {t('policyInfoAndRates:no-endorsement')}
            </Typography>
            )}

            {endorsementsOptions.map((endorsement) => (
              <CardActionArea
                key={endorsement.code}
                id={`policy_info_and_rates.${selectedPolicy}.endorsements`}
                className={classes.cardItemContainer}
                onClick={() => handleAddendorsement(endorsement)}
              >
                <Typography className={classes.cardText}>{`${endorsement.description}`}</Typography>
              </CardActionArea>
            ))}
          </CardContent>
        </Card>
      </Popover>
    </Grid>
  );
};

export default EndorsementSearch;
